import React, { useState, useEffect } from 'react';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { copyTextToClipboard } from '../utils/clipboard';

export default function Generator() {
  const [length, setLength] = useState(16);
  const [useUppercase, setUseUppercase] = useState(true);
  const [useLowercase, setUseLowercase] = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [password, setPassword] = useState('');
  const { clipboardClearTimeoutMs } = useAuth();

  const generate = () => {
    let chars = '';
    if (useUppercase) {
      chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    }
    if (useLowercase) {
      chars += 'abcdefghijklmnopqrstuvwxyz';
    }
    if (useNumbers) {
      chars += '0123456789';
    }
    if (useSymbols) {
      chars += '!@#$%^&*()_+~`|}{[]:;?><,./-=';
    }

    if (!chars) {
      setPassword('');
      return;
    }

    let result = '';
    // Use Web Crypto API for secure randomness
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
    setPassword(result);
  };

  useEffect(() => {
    generate();
  }, [length, useUppercase, useLowercase, useNumbers, useSymbols]);

  const copyToClipboard = () => {
    if (password) {
      copyTextToClipboard(password, clipboardClearTimeoutMs);
    }
  };

  return (
    <MainLayout>
      <div className="page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">Password Generator</h1>
            <p className="notes-hero-subtitle">Generate high-entropy, cryptographically strong passwords and passphrases.</p>
          </div>
        </div>
      </div>
      
      <div className="glass-panel" style={{ padding: '32px', maxWidth: '600px', width: '100%' }}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'stretch' }}>
          <div style={{ 
            flex: 1, 
            fontSize: '18px', /* Reduced font size to accommodate longer passwords */
            fontFamily: 'monospace', 
            padding: '16px', 
            background: 'rgba(0,0,0,0.05)', /* Lighter background for better contrast in light mode */
            borderRadius: '8px', 
            border: '1px solid var(--border-color)', 
            color: 'var(--accent-primary)', 
            textAlign: 'center', 
            letterSpacing: '1px',
            wordBreak: 'break-all', /* Crucial: allows long strings without spaces to wrap */
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60px' /* Ensure consistent height */
          }}>
            {password || 'Select options'}
          </div>
          <Button onClick={copyToClipboard} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px', flexShrink: 0 }}>
            <Copy size={20} />
          </Button>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <label className="input-label">Length</label>
            <span>{length}</span>
          </div>
          <input 
            type="range" 
            min="8" max="64" 
            value={length} 
            onChange={(e) => setLength(parseInt(e.target.value))} 
            style={{ width: '100%', cursor: 'pointer' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={useUppercase} onChange={(e) => setUseUppercase(e.target.checked)} style={{ width: '20px', height: '20px' }} />
            <span>Uppercase (A-Z)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={useLowercase} onChange={(e) => setUseLowercase(e.target.checked)} style={{ width: '20px', height: '20px' }} />
            <span>Lowercase (a-z)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={useNumbers} onChange={(e) => setUseNumbers(e.target.checked)} style={{ width: '20px', height: '20px' }} />
            <span>Numbers (0-9)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={useSymbols} onChange={(e) => setUseSymbols(e.target.checked)} style={{ width: '20px', height: '20px' }} />
            <span>Symbols (!@#$%)</span>
          </label>
        </div>

        <Button onClick={generate} style={{ width: '100%', marginTop: '32px' }}>
          Regenerate
        </Button>
      </div>
    </MainLayout>
  );
}
